import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Smartphone, Copy, Check, KeyRound, X,
} from "lucide-react";

export default function AgentTwoFactor() {
  const [status, setStatus] = useState({ enabled: false, backup_remaining: 0, loading: true });
  const [setupData, setSetupData] = useState(null);
  const [step, setStep] = useState("idle");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const reload = async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/agent/auth/2fa/status");
      setStatus({ ...data, loading: false });
    } catch {
      setStatus({ enabled: false, backup_remaining: 0, loading: false });
    }
  };
  useEffect(() => { reload(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agent/auth/2fa/setup");
      setSetupData(data);
      setStep("scan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start 2FA setup");
    } finally { setBusy(false); }
  };

  const enable = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    if (!password) return toast.error("Enter your agent password");
    setBusy(true);
    try {
      await api.post("/agent/auth/2fa/enable", {
        password, secret: setupData.secret,
        totp_code: otp, backup_codes: setupData.backup_codes,
      });
      toast.success("Two-factor authentication enabled");
      setStep("backup");
      setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enable 2FA");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!password) return toast.error("Enter your password");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/agent/auth/2fa/disable", { password, totp_code: otp });
      toast.success("Two-factor authentication disabled");
      setSetupData(null); setStep("idle"); setOtp(""); setPassword("");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable 2FA");
    } finally { setBusy(false); }
  };

  const copy = (txt, key) => {
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = txt; ta.setAttribute("readonly", "");
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        return true;
      } catch (_) { return false; }
    };
    const ok = (() => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(txt).catch(fallback);
          return true;
        }
      } catch (_) { /* ignore */ }
      return fallback();
    })();
    if (ok) { setCopied(key); setTimeout(() => setCopied(null), 1500); }
    else { toast.error("Copy not allowed — please copy manually"); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="agent-2fa-page">
      <div className="mb-6">
        <h1 className="text-2xl font-black tracking-tight">Two-Factor Authentication</h1>
        <p className="text-white/55 text-sm mt-1">
          Add Google Authenticator (TOTP) so your login requires a 6-digit code alongside your password.
        </p>
      </div>

      <div className="rounded-xl bg-[#0d1422]/90 border border-white/[0.07] p-6 backdrop-blur-xl" data-testid="agent-2fa-card">
        <div className="flex items-center gap-3 pb-4 border-b border-white/[0.06] mb-5">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
            status.enabled
              ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
              : "bg-amber-500/15 text-amber-400 border border-amber-500/30"
          }`}>
            {status.enabled ? <ShieldCheck size={20} /> : <ShieldAlert size={20} />}
          </div>
          <div className="flex-1">
            <div className="text-base font-bold">Google Authenticator</div>
            <div className="text-xs text-white/50">
              After enabling, login will ask for a 6-digit code from your app.
            </div>
          </div>
          {!status.loading && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
              status.enabled
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
            }`} data-testid="agent-2fa-status-badge">
              {status.enabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {step === "idle" && !status.enabled && !status.loading && (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
              Your agent account is currently <b>not protected</b> by 2FA. Enable it now — takes ~30 seconds.
            </div>
            <button onClick={startSetup} disabled={busy}
              data-testid="agent-2fa-start-setup"
              className="h-10 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60">
              <Smartphone size={14} /> {busy ? "Loading…" : "Set up 2FA"}
            </button>
          </div>
        )}

        {step === "idle" && status.enabled && (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-xs text-emerald-200">
              Your account is protected by 2FA. <b>{status.backup_remaining}</b> backup codes remaining.
            </div>
            <button onClick={() => setStep("disable")}
              data-testid="agent-2fa-disable-start"
              className="h-10 px-4 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 text-xs font-bold inline-flex items-center gap-1.5">
              <ShieldAlert size={13} /> Disable 2FA
            </button>
          </div>
        )}

        {step === "scan" && setupData && (
          <div className="space-y-4" data-testid="agent-2fa-scan-step">
            <div className="bg-[#070b14] border border-white/[0.06] rounded-lg p-4 flex flex-col sm:flex-row items-center gap-4">
              <img src={setupData.qr_data_url} alt="2FA QR"
                className="h-44 w-44 rounded-md bg-white p-1.5" />
              <div className="text-xs text-white/65 space-y-2 flex-1">
                <p className="font-semibold text-white/90">1. Scan with Google Authenticator</p>
                <p className="text-[11px] text-white/45">Or enter the secret manually:</p>
                <div className="bg-black/40 border border-white/10 rounded px-2 py-1.5 font-mono text-[12px] text-white/90 flex items-center gap-2">
                  <span className="flex-1 break-all">{setupData.secret}</span>
                  <button type="button" onClick={() => copy(setupData.secret, "sec")} className="text-emerald-300 shrink-0">
                    {copied === "sec" ? <Check size={13} /> : <Copy size={13} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-white/65 font-semibold">2. Enter your password and 6-digit code</p>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Agent password"
                className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white placeholder:text-white/30 focus:border-emerald-500 focus:outline-none"
                data-testid="agent-2fa-enable-password" />
              <input type="text" inputMode="numeric" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-3 text-center text-2xl tracking-[0.4em] font-mono text-white focus:border-emerald-500 focus:outline-none"
                data-testid="agent-2fa-enable-code" />
            </div>
            <div className="flex gap-2">
              <button onClick={enable} disabled={busy || otp.length !== 6 || !password}
                className="flex-1 h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black text-sm font-bold disabled:opacity-60"
                data-testid="agent-2fa-enable-confirm">
                {busy ? "Enabling…" : "Verify & enable"}
              </button>
              <button onClick={() => { setStep("idle"); setSetupData(null); setOtp(""); setPassword(""); }}
                className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm inline-flex items-center gap-1.5">
                <X size={13} /> Cancel
              </button>
            </div>
          </div>
        )}

        {step === "backup" && setupData?.backup_codes && (
          <div className="space-y-4" data-testid="agent-2fa-backup-step">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[12px] text-amber-200 flex gap-2 items-start">
              <KeyRound size={14} className="mt-0.5 shrink-0" />
              <span><b>Save these backup codes.</b> Each can only be used once. They will not be shown again.</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {setupData.backup_codes.map((c) => (
                <div key={c} className="bg-[#070b14] border border-white/10 rounded px-2.5 py-2 font-mono text-[13px] text-white/90 flex items-center gap-1.5">
                  <span className="flex-1">{c}</span>
                  <button onClick={() => copy(c, c)} className="text-emerald-300/70 shrink-0">
                    {copied === c ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => copy(setupData.backup_codes.join("\n"), "all")}
                className="h-10 px-3 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/85 inline-flex items-center gap-1.5">
                {copied === "all" ? <Check size={12} /> : <Copy size={12} />} Copy all
              </button>
              <button onClick={() => { setStep("idle"); setSetupData(null); }}
                className="flex-1 h-10 rounded-md bg-emerald-500 hover:bg-emerald-600 text-black text-xs font-bold"
                data-testid="agent-2fa-backup-done">
                I've saved them
              </button>
            </div>
          </div>
        )}

        {step === "disable" && (
          <div className="space-y-3" data-testid="agent-2fa-disable-step">
            <p className="text-xs text-rose-300">Confirm your agent password and a current 6-digit code to disable 2FA.</p>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Agent password"
              className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-white" data-testid="agent-2fa-disable-password" />
            <input type="text" inputMode="numeric" value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000" maxLength={6}
              className="w-full h-11 rounded-lg bg-[#101824] border border-white/10 px-3 text-center text-xl tracking-[0.4em] font-mono text-white"
              data-testid="agent-2fa-disable-code" />
            <div className="flex gap-2">
              <button onClick={disable} disabled={busy || otp.length !== 6 || !password}
                className="flex-1 h-11 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold disabled:opacity-60"
                data-testid="agent-2fa-disable-confirm">
                {busy ? "Disabling…" : "Disable 2FA"}
              </button>
              <button onClick={() => { setStep("idle"); setOtp(""); setPassword(""); }}
                className="h-11 px-4 rounded-lg bg-white/5 border border-white/10 text-sm text-white/70 inline-flex items-center gap-1.5">
                <X size={13} /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
